import { useCallback } from 'react';

import { Plus } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import {
  FormControl,
  FormItem,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/_components';
import { ActionButton } from '@/_containers/actions';
import { TableHeading } from '@/_containers/data-table';
import { allowedFileTypes, cs, snackbar } from '@/_shared';

import { FileRow } from './file-row';
import { SingleFileView } from './single-file-view';
import type { FormFilesInputProps } from './types';
import { FilesUploadModal } from './upload-modal';
import { useFilesInput } from './use-files-input';

const IMAGE_TYPES = [...allowedFileTypes.imageOnly];
const DEFAULT_ALLOWED_TYPES = [...allowedFileTypes.all];

export function FormFilesInput<T>({
  label,
  value = [],
  onChange,
  disabled = false,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  maxFiles = 100,
  formItemClassName,
  shape = 'rectangle',
  size = 'md',
  folder_path = '',
  access_level = 'private',
  onPartialUpdate,
  imagesOnly = false,
  emptyPlaceholder,
  allowDelete = true,
  emptyMessage = 'No files uploaded. Click "+" to add files.',
}: FormFilesInputProps<T>) {
  const effectiveAllowedTypes = imagesOnly ? IMAGE_TYPES : allowedTypes || DEFAULT_ALLOWED_TYPES;

  const {
    files,
    selectedFiles,
    isPending,
    modalOpen,
    inputRef,
    isSingleFile,
    isMediaUpdatePending,
    handleFileSelect,
    handleUpload,
    handleCancel,
    handleRemove,
    handleRemoveSelected,
    handleUpdate,
  } = useFilesInput({
    value,
    onChange,
    maxFiles,
    allowedTypes: effectiveAllowedTypes,
    shape,
    size,
    folder_path,
    access_level,
    onPartialUpdate,
    imagesOnly,
  });

  const isAddMoreDisabled = !maxFiles || files.length >= maxFiles;

  const handleFileSelectClick = useCallback(() => {
    if (isAddMoreDisabled) {
      snackbar.error('Max files limit reached!');
      return;
    }
    inputRef.current?.click();
  }, [inputRef, isAddMoreDisabled]);

  const handleAddMoreFiles = useCallback(() => {
    inputRef.current?.click();
  }, [inputRef]);

  return (
    <section className={twMerge('flex w-full items-center', formItemClassName)}>
      <input
        ref={inputRef}
        type="file"
        multiple={!isSingleFile}
        accept={effectiveAllowedTypes.join(',')}
        className="hidden"
        onChange={handleFileSelect}
      />
      <FormItem className="relative w-full">
        <FormControl>
          <div>
            {label && (
              <FormLabel
                className={twMerge(
                  'absolute -top-7 left-0 text-xs text-gray-400',
                  maxFiles === 1 || (maxFiles === 1 && files.length === 0) ? '-top-5' : ''
                )}
              >
                {label}
              </FormLabel>
            )}

            {isSingleFile ? (
              <SingleFileView
                file={files[0]}
                onRemove={handleRemove}
                onAdd={handleFileSelectClick}
                disabled={disabled}
                loading={isPending || isMediaUpdatePending}
                emptyPlaceholder={emptyPlaceholder}
                onUpdate={handleUpdate}
                allowDelete={allowDelete}
              />
            ) : (
              <div className={twMerge('border-c-border border', 'rounded-xl', cs('rounded-4xl'))}>
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
                      <TableHeading title="Name" align="left" className="pl-2" />
                      <TableHeading title="Size" align="right" />
                      <TableHead className={twMerge('w-16 shrink-0 pr-2 text-right', !disabled && 'w-32')}>
                        {!disabled && (
                          <ActionButton
                            size="sm"
                            title={isAddMoreDisabled ? 'Max Files Reached' : 'Add File'}
                            tag="button"
                            icon={Plus}
                            type="primary"
                            onClick={handleFileSelectClick}
                            disabled={isAddMoreDisabled}
                          />
                        )}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {files.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-c-card-muted h-24 rounded-b-2xl text-center">
                          {emptyMessage}
                        </TableCell>
                      </TableRow>
                    ) : (
                      files.map((file, index) => (
                        <FileRow
                          key={file.id}
                          file={file}
                          onRemove={handleRemove}
                          disabled={disabled}
                          onUpdate={handleUpdate}
                          allowDelete={allowDelete}
                          isLastItem={files.length - 1 === index}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {!isSingleFile && (
              <FilesUploadModal
                files={selectedFiles}
                isPending={isPending}
                modelOpen={modalOpen}
                onUpload={handleUpload}
                onCancel={handleCancel}
                disabled={disabled}
                onRemove={handleRemoveSelected}
                onAddMore={isAddMoreDisabled ? undefined : handleAddMoreFiles}
              />
            )}
          </div>
        </FormControl>
      </FormItem>
    </section>
  );
}
